package app.footyguide.web.page

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.web.components.*
import app.footyguide.web.theme.FootyTheme
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.Team
import app.footyguide.domain.model.isLive
import app.footyguide.web.data.WebGameRepository
import app.footyguide.web.data.WebTeamRepository
import app.footyguide.web.data.WebChannelRepository
import app.footyguide.web.data.WebCompetitionRepository
import app.footyguide.web.data.HighlightlyLiveRefreshApi
import app.footyguide.web.route.navigateTo
import app.footyguide.web.theme.Radius
import app.footyguide.core.locale.LocalStrings
import kotlinx.datetime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

@Composable
fun HomePage(
    gameRepository: WebGameRepository,
    teamRepository: WebTeamRepository,
    competitionRepository: WebCompetitionRepository,
    channelRepository: WebChannelRepository,
    today: String,
    initialDate: String? = null,
    onDateSelected: ((String) -> Unit)? = null,
    onNavigate: ((String) -> Unit)? = null,
    mockTeams: Map<String, Team>? = null
) {
    val strings = LocalStrings.current
    val todayDate = remember { LocalDate.parse(today) }
    var selectedDate by remember(todayDate, initialDate) {
        mutableStateOf(defaultSelectedDate(todayDate, initialDate))
    }
    var games by remember { mutableStateOf<List<Game>>(emptyList()) }
    var nextDateWithGames by remember { mutableStateOf<String?>(null) }
    var nextTeamGame by remember { mutableStateOf<Game?>(null) }
    var nextBrazilGame by remember { mutableStateOf<Game?>(null) }
    var isLoadingGames by remember { mutableStateOf(false) }
    
    // Sincroniza o estado interno se a URL mudar (initialDate ou retorno à raiz)
    LaunchedEffect(initialDate, todayDate) {
        val target = if (initialDate != null) {
            try { LocalDate.parse(initialDate) } catch (_: Exception) { todayDate }
        } else {
            todayDate
        }
        if (target != selectedDate) {
            selectedDate = target
        }
    }
    var teams by remember { mutableStateOf<Map<String, Team>>(mockTeams ?: emptyMap()) }
    var brasileiraoTeamIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var competitions by remember { mutableStateOf<Map<String, WebCompetitionRepository.CompetitionData>>(emptyMap()) }
    var channels by remember { mutableStateOf<Map<String, WebChannelRepository.ChannelData>>(emptyMap()) }
    var collapsedSections by remember { mutableStateOf(setOf<String>()) }
    
    // UI State
    var showTeamModal by remember { mutableStateOf(false) }
    var showCalendarModal by remember { mutableStateOf(false) }
    var selectedTeamId by remember { mutableStateOf(window.localStorage.getItem("fg-my-team")) }
    val mainContentId = "fg-home-main"

    var scrollerDates by remember(todayDate) { mutableStateOf(buildInitialScrollerDates(todayDate)) }
    val initialScrollerFocusDate = remember(todayDate, initialDate) {
        initialDate?.let { LocalDate.parse(it) }
    }
    fun ensureDatesUpTo(date: LocalDate) {
        val first = scrollerDates.firstOrNull() ?: return
        val last = scrollerDates.lastOrNull() ?: return
        if (date < first) {
            val daysDiff = (first - date).days.coerceAtLeast(1)
            val newDates = (1..daysDiff).reversed().map { first.minus(DatePeriod(days = it)) }
            scrollerDates = newDates + scrollerDates
        } else if (date > last) {
            val daysDiff = (date - last).days.coerceAtLeast(1)
            val newDates = (1..daysDiff).map { last.plus(DatePeriod(days = it)) }
            scrollerDates = scrollerDates + newDates
        }
    }
    fun loadPreviousDates() {
        scrollerDates = prependScrollerDates(scrollerDates)
    }
    fun loadMoreDates() {
        val last = scrollerDates.last()
        val newDates = (1..14).map { last.plus(DatePeriod(days = it)) }
        scrollerDates = scrollerDates + newDates
    }

    // Initial data load: Teams and Competitions (once)
    LaunchedEffect(Unit) {
        if (mockTeams == null) {
            try {
                val teamDataMap = teamRepository.getAll()
                brasileiraoTeamIds = teamDataMap.filterValues { "brasileirao" in it.competitions }.keys
                teams = teamDataMap.mapValues { (_, data) ->
                    Team(
                        id = data.id,
                        name = data.name,
                        shortName = data.shortName,
                        nameByLocale = data.nameByLocale,
                        country = data.country,
                        league = data.competitions.firstOrNull() ?: "brasileirao",
                        logo = data.logo,
                        primaryColor = data.primaryColor,
                        secondaryColor = data.secondaryColor,
                        tertiaryColor = data.tertiaryColor,
                        isDark = data.isDark
                    )
                }
            } catch (e: Exception) {
                console.error("[HomePage] Erro ao carregar times: ${e.message}")
            }
        }
        
        try {
            competitions = competitionRepository.getAll()
        } catch (e: Exception) {
            console.error("[HomePage] Erro ao carregar competições: ${e.message}")
        }

        try {
            channels = channelRepository.getAll()
        } catch (e: Exception) {
            console.error("[HomePage] Erro ao carregar canais: ${e.message}")
        }
    }

    // Date-based game load and next game date resolution
    LaunchedEffect(selectedDate) {
        nextDateWithGames = null
        isLoadingGames = true
        try {
            val loadedGames = gameRepository.getGamesByDate(selectedDate.toString())
            games = loadedGames
            if (loadedGames.isEmpty()) {
                try {
                    val next = gameRepository.findNextGameDate(selectedDate.toString())
                    if (next != null && next != selectedDate.toString()) {
                        nextDateWithGames = next
                    }
                } catch (e: Exception) {
                    console.error("[HomePage] Erro ao buscar próxima data: ${e.message}")
                }
            }
        } catch (e: Exception) {
            console.error("[HomePage] Erro ao buscar jogos: ${e.message}")
        } finally {
            isLoadingGames = false
        }
    }

    // Busca próximo jogo do time favorito
    LaunchedEffect(selectedTeamId, selectedDate) {
        nextTeamGame = null
        if (selectedTeamId != null) {
            try {
                nextTeamGame = gameRepository.findNextGameByTeam(selectedTeamId!!, selectedDate.toString())
            } catch (e: Exception) {
                console.error("[HomePage] Erro ao buscar próximo jogo do time: ${e.message}")
            }
        }
    }

    LaunchedEffect(selectedDate, teams) {
        nextBrazilGame = null
        if (teams.isNotEmpty()) {
            try {
                val worldCupGames = gameRepository.getGamesByCompetition(
                    "worldCup",
                    selectedDate.toString(),
                    selectedDate.plus(DatePeriod(days = 365)).toString(),
                )
                nextBrazilGame = worldCupGames
                    .filter { isBrazilWorldCupGame(it, teams) }
                    .minByOrNull { it.kickoff }
            } catch (e: Exception) {
                console.error("[HomePage] Erro ao buscar próximo jogo do Brasil: ${e.message}")
            }
        }
    }

    LaunchedEffect(selectedTeamId, teams) {
        if (selectedTeamId != null) {
            window.localStorage.setItem("fg-my-team", selectedTeamId!!)
            teams[selectedTeamId]?.let { team ->
                FootyTheme.fromTeamColors(
                    team.primaryColor,
                    team.secondaryColor,
                    team.tertiaryColor,
                    team.isDark
                ).apply()
            } ?: FootyTheme.applyStored()
        } else {
            FootyTheme.applyStored()
        }
    }

    val currentSelectedDate = rememberUpdatedState(selectedDate)
    val onDateSelectedState = rememberUpdatedState(onDateSelected)

    DisposableEffect(Unit) {
        var startX = 0.0
        var startY = 0.0
        var tracking = false

        fun onTouchStart(event: Event) {
            val native = event.asDynamic()
            val source = native.target as? HTMLElement
            if (!isHomeGamesListSwipeTarget(source)) return
            val touch = native.touches[0] ?: return
            startX = touch.clientX.unsafeCast<Double>()
            startY = touch.clientY.unsafeCast<Double>()
            tracking = true
        }

        fun onTouchEnd(event: Event) {
            if (!tracking) return
            tracking = false
            val native = event.asDynamic()
            val touch = native.changedTouches[0] ?: return
            val clientX = touch.clientX.unsafeCast<Double>()
            val clientY = touch.clientY.unsafeCast<Double>()
            val deltaX = clientX - startX
            val deltaY = clientY - startY
            val threshold = 50.0
            if (kotlin.math.abs(deltaX) < threshold || kotlin.math.abs(deltaX) < kotlin.math.abs(deltaY) * 1.2) return
            val cur = currentSelectedDate.value
            val nextDate = if (deltaX < 0) {
                cur.plus(DatePeriod(days = 1))
            } else {
                cur.minus(DatePeriod(days = 1))
            }
            ensureDatesUpTo(nextDate)
            selectedDate = nextDate
            onDateSelectedState.value?.invoke(nextDate.toString())
        }

        window.addEventListener("touchstart", ::onTouchStart)
        window.addEventListener("touchend", ::onTouchEnd)
        window.asDynamic().__fgOpenTeamModal = {
            showTeamModal = true
            try { androidx.compose.runtime.snapshots.Snapshot.sendApplyNotifications() } catch (_: Throwable) {}
        }
        onDispose {
            window.removeEventListener("touchstart", ::onTouchStart)
            window.removeEventListener("touchend", ::onTouchEnd)
            window.asDynamic().__fgOpenTeamModal = null
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            backgroundColor(Color("var(--bg)"))
            property("transition", "background-color 0.8s ease")
        }
    }) {
        Toolbar(
            onSearchClick = { /* TODO */ },
            dateLabel = formatSelectedDateLabel(selectedDate),
            onDateLabelClick = { showCalendarModal = true },
            teamBadge = {
                Div({
                    onClick {
                        showTeamModal = true
                        try { androidx.compose.runtime.snapshots.Snapshot.sendApplyNotifications() } catch (_: Throwable) {}
                    }
                    style {
                        width(40.px); height(40.px)
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                        cursor("pointer")
                    }
                }) {
                    val teamLogo = selectedTeamId?.let { teams[it]?.logo }
                    SafeImg(
                        src = teamLogo,
                        fallbackIcon = Icons.SHIELD,
                        fallbackSize = CrestSizes.TOOLBAR_FALLBACK,
                        modifier = {
                            width(CrestSizes.TOOLBAR_PX.px)
                            height(CrestSizes.TOOLBAR_PX.px)
                            property("width", CrestSizes.TOOLBAR_CSS)
                            property("height", CrestSizes.TOOLBAR_CSS)
                            property("object-fit", "contain")
                        }
                    )
                }
            }
        )

        Div({
            style {
                width(100.percent); maxWidth(600.px)
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); backgroundColor(Color("var(--bg)"))
                paddingBottom(72.px)
                property("transition", "background-color 0.8s ease")
            }
        }) {
            Main({
                id(mainContentId)
                style { flex(1); paddingBottom(40.px) }
            }) {
                DateScroller(
                    dates = scrollerDates,
                    selectedDate = selectedDate,
                    today = todayDate,
                    onDateSelected = {
                        selectedDate = it
                        onDateSelected?.invoke(it.toString())
                    },
                    onOpenPicker = { showCalendarModal = true },
                    initialScrollDate = initialScrollerFocusDate,
                    onLoadPrevious = { loadPreviousDates() },
                    onLoadMore = { loadMoreDates() }
                )

                HeartTeamBanner(selectedTeamId = selectedTeamId) {
                    showTeamModal = true
                    try { androidx.compose.runtime.snapshots.Snapshot.sendApplyNotifications() } catch (_: Throwable) {}
                }

                if (selectedTeamId != null && nextTeamGame != null) {
                    NextGameBanner(
                        game = nextTeamGame!!,
                        team = resolveTeam(teams, selectedTeamId!!),
                        teams = teams,
                        competitions = competitions,
                        channels = channels,
                        onClick = {
                            val route = buildGameRoute(nextTeamGame!!)
                            onNavigate?.invoke(route) ?: navigateTo(route)
                        }
                    )
                }

                if (nextBrazilGame != null && nextBrazilGame!!.id != nextTeamGame?.id) {
                    NextGameBanner(
                        game = nextBrazilGame!!,
                        team = resolveTeam(teams, "brasil"),
                        teams = teams,
                        competitions = competitions,
                        channels = channels,
                        onClick = {
                            val route = buildGameRoute(nextBrazilGame!!)
                            onNavigate?.invoke(route) ?: navigateTo(route)
                        }
                    )
                }

                val activeGames = games
                    .filter { competitions.containsKey(it.competitionId) }
                    .sortedBy { it.kickoff }
                val grouped = activeGames
                    .groupBy { it.competitionId }
                    .entries
                    .sortedWith(
                        compareBy<Map.Entry<String, List<Game>>> { entry ->
                            competitions[entry.key]?.priority ?: Int.MAX_VALUE
                        }.thenBy { entry ->
                            entry.value.firstOrNull()?.kickoff ?: ""
                        },
                    )
                val liveGroups = grouped.filter { entry -> entry.value.any { it.isLive } }

                liveGroups.forEachIndexed { index, entry ->
                    val compId = entry.key
                    val leaseKey = buildLiveRefreshKey(selectedDate.toString(), compId)
                    LiveRefreshEffect(
                        enabled = shouldEnableAgendaLiveRefresh(selectedDate, todayDate, entry.value),
                        leaseKey = leaseKey,
                        watchPath = if (index == 0) "games/${selectedDate}/matches" else null,
                        intervalMs = DEFAULT_LIVE_REFRESH_INTERVAL_MS,
                        onRefresh = {
                            if (!HighlightlyLiveRefreshApi.refresh(
                                date = selectedDate.toString(),
                                competitionId = compId,
                                ttlMinutes = DEFAULT_LIVE_REFRESH_TTL_MINUTES,
                            )) {
                                error("live refresh failed")
                            }
                            games = gameRepository.getGamesByDate(selectedDate.toString())
                        },
                        onBroadcastRefresh = { liveScores ->
                            if (liveScores.isEmpty()) {
                                gameRepository.refreshGamesByDate(selectedDate.toString())
                                    .takeIf { it.isNotEmpty() }
                                    ?.let { games = it }
                            } else {
                                games = gameRepository.applyLiveScores(games, liveScores)
                            }
                        },
                    )
                }
                
                if (isLoadingGames) {
                    SkeletonGamesList(count = 3)
                } else if (grouped.isEmpty()) {
                    Div({
                        style {
                            margin(28.px, 16.px)
                            padding(32.px, 16.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            alignItems(AlignItems.Center)
                            property("text-align", "center")
                            gap(10.px)
                        }
                    }) {
                        // Radar Pulse animado em torno do ícone central
                        Div({
                            style {
                                position(Position.Relative)
                                width(64.px)
                                height(64.px)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                marginBottom(6.px)
                            }
                        }) {
                            // Anel concêntrico 1
                            Div({
                                style {
                                    position(Position.Absolute)
                                    width(48.px)
                                    height(48.px)
                                    borderRadius(50.percent)
                                    border(1.px, LineStyle.Solid, Color("var(--accent-fill, #0B6E4F)"))
                                    property("animation", "fg-empty-radar-pulse 2.4s cubic-bezier(0.16, 1, 0.3, 1) infinite")
                                    property("pointer-events", "none")
                                }
                            })
                            // Anel concêntrico 2 (com delay)
                            Div({
                                style {
                                    position(Position.Absolute)
                                    width(48.px)
                                    height(48.px)
                                    borderRadius(50.percent)
                                    border(1.px, LineStyle.Solid, Color("var(--accent-fill, #0B6E4F)"))
                                    property("animation", "fg-empty-radar-pulse 2.4s cubic-bezier(0.16, 1, 0.3, 1) 1.2s infinite")
                                    property("pointer-events", "none")
                                }
                            })
                            // Núcleo estático com contraste no tema paper
                            Div({
                                style {
                                    width(48.px)
                                    height(48.px)
                                    borderRadius(50.percent)
                                    backgroundColor(Color("rgba(22, 24, 29, 0.05)"))
                                    border(1.px, LineStyle.Solid, Color("var(--border, #E7E7E1)"))
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    property("z-index", "1")
                                }
                            }) {
                                LucideIcon(Icons.CALENDAR, size = 22, color = "var(--accent-fill, #0B6E4F)")
                            }
                        }

                        H3({
                            style {
                                margin(0.px)
                                fontSize(16.px)
                                fontWeight(700)
                                color(Color("var(--fg, #16181D)"))
                                letterSpacing((-0.2).px)
                            }
                        }) {
                            Text(strings.noGamesScheduled)
                        }

                        P({
                            style {
                                margin(0.px)
                                color(Color("var(--muted, #A1A1AA)"))
                                fontSize(13.px)
                                property("line-height", "1.4")
                                maxWidth(300.px)
                            }
                        }) {
                            Text(strings.noGamesForDate)
                        }

                        val targetNextDate = nextDateWithGames?.let {
                            try { LocalDate.parse(it) } catch (_: Exception) { null }
                        }

                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                                marginTop(14.px)
                            }
                        }) {
                            if (targetNextDate != null) {
                                val nextLabel = formatNextDateLabel(targetNextDate)
                                Button({
                                    onClick {
                                        ensureDatesUpTo(targetNextDate)
                                        selectedDate = targetNextDate
                                        onDateSelected?.invoke(targetNextDate.toString())
                                    }
                                    style {
                                        padding(10.px, 22.px)
                                        backgroundColor(Color("var(--accent-fill, #0B6E4F)"))
                                        color(Color("var(--accent-on-fill, #FFFFFF)"))
                                        borderRadius(999.px)
                                        border(0.px)
                                        cursor("pointer")
                                        fontWeight(700)
                                        fontSize(13.px)
                                        property("display", "inline-flex")
                                        alignItems(AlignItems.Center)
                                        gap(8.px)
                                        property("box-shadow", "0 4px 16px rgba(0, 0, 0, 0.3)")
                                        property("transition", "transform 0.15s ease, filter 0.15s ease")
                                    }
                                }) {
                                    Span({
                                        style {
                                            color(Color("var(--accent-on-fill, #FFFFFF)"))
                                        }
                                    }) {
                                        Text("${strings.viewGamesForDate} $nextLabel")
                                    }
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.Center)
                                            property("animation", "fg-chevron-nudge 1.6s ease-in-out infinite")
                                        }
                                    }) {
                                        LucideIcon(Icons.CHEVRON_RIGHT, size = 16, color = "var(--accent-on-fill, #FFFFFF)")
                                    }
                                }
                            }

                            // Atalho secundário: Voltar para Hoje
                            if (selectedDate != todayDate) {
                                Button({
                                    onClick {
                                        ensureDatesUpTo(todayDate)
                                        selectedDate = todayDate
                                        onDateSelected?.invoke(todayDate.toString())
                                    }
                                    style {
                                        padding(6.px, 14.px)
                                        backgroundColor(Color("transparent"))
                                        color(Color("var(--muted, #A1A1AA)"))
                                        borderRadius(8.px)
                                        border(0.px)
                                        cursor("pointer")
                                        fontWeight(600)
                                        fontSize(12.px)
                                        property("transition", "color 0.15s ease")
                                    }
                                }) {
                                    Text(strings.backToToday)
                                }
                            }
                        }
                    }
                } else {
                    grouped.forEach { entry ->
                        val compId = entry.key
                        val compGames = entry.value.sortedBy { game -> game.kickoff }
                        val comp = competitions[compId]
                        val isCollapsed = collapsedSections.contains(compId)

                        Div({
                            onClick {
                                collapsedSections = if (isCollapsed) collapsedSections - compId else collapsedSections + compId
                            }
                            style { padding(20.px, 24.px, 10.px, 16.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); cursor("pointer") }
                        }) {
                            SafeImg(
                                src = comp?.logo,
                                fallbackIcon = Icons.SHIELD,
                                fallbackSize = 14,
                                modifier = {
                                    width(18.px); height(18.px); property("object-fit", "contain")
                                }
                            )
                            Span({
                                style {
                                    marginLeft(10.px); fontSize(12.px); fontWeight(800)
                                    fontFamily("Sora, sans-serif")
                                    property("text-transform", "uppercase")
                                    letterSpacing(0.6.px); color(Color("var(--fg)"))
                                }
                            }) {
                                Text(competitionDisplayName(compId, comp?.shortName, comp?.name))
                            }
                            Div({
                                style {
                                    flex(1); height(1.px); backgroundColor(Color("var(--border)"))
                                    margin(0.px, 12.px)
                                }
                            })
                            LucideIcon(
                                name = if (isCollapsed) Icons.CHEVRON_RIGHT else Icons.CHEVRON_DOWN,
                                size = 14,
                                color = "var(--muted)"
                            )
                        }

                        if (!isCollapsed) {
                            Div({
                                classes("fg-home-games-list")
                                style { padding(0.px, 16.px) }
                            }) {
                                compGames.forEach { game ->
                                    val homeTeam = resolveTeam(teams, game.homeTeamId)
                                    val awayTeam = resolveTeam(teams, game.awayTeamId)
                                    
                                    if (homeTeam == null || awayTeam == null) {
                                        
                                        val availableIds = teams.keys.toList()
                                        if (availableIds.isNotEmpty()) {
                                            // Tenta sugerir um match se o problema for prefixo
                                            val suggestion = availableIds.find { it.contains(game.homeTeamId) || game.homeTeamId.contains(it) }
                                        } else {
                                            console.error("  - O cache de times está VAZIO!")
                                        }
                                    }

                                    MatchCard(
                                        game = game,
                                        homeSigla = homeTeam?.shortName ?: game.homeTeamId.take(3).uppercase(),
                                        awaySigla = awayTeam?.shortName ?: game.awayTeamId.take(3).uppercase(),
                                        homeLogo = homeTeam?.logo,
                                        awayLogo = awayTeam?.logo,
                                        homeTeam = homeTeam,
                                        awayTeam = awayTeam,
                                        channelLogos = channels.mapValues { (_, data) -> data.logo },
                                        onClick = {
                                            val route = buildGameRoute(game)
                                            onNavigate?.invoke(route) ?: navigateTo(route)
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
        
        if (showTeamModal) {
            TeamSelectionModal(
                serieAIds = WebTeamRepository.SERIE_A_IDS,
                teams = teams,
                currentTeamId = selectedTeamId,
                onTeamSelected = { id ->
                    if (id.isBlank()) {
                        selectedTeamId = null
                        window.localStorage.removeItem("fg-my-team")
                        FootyTheme.applyStored()
                    } else {
                        selectedTeamId = id
                        window.localStorage.setItem("fg-my-team", id)
                    }
                },
                onClose = { showTeamModal = false }
            )
        }
        
        if (showCalendarModal) {
            CustomCalendarModal(
                initialDate = selectedDate,
                today = todayDate,
                onDateSelected = { date ->
                    ensureDatesUpTo(date)
                    selectedDate = date
                    onDateSelected?.invoke(date.toString())
                    showCalendarModal = false
                },
                onClose = { showCalendarModal = false }
            )
        }

        BottomNavBar(
            currentRoute = "/",
            onNavigate = { route ->
                onNavigate?.invoke(route) ?: navigateTo(route)
            }
        )
    }
}

internal fun isHomeGamesListSwipeTarget(target: HTMLElement?): Boolean =
    target?.closest(".fg-home-games-list") != null

private fun formatNextDateLabel(date: LocalDate): String {
    val d = js("new Date(2000, 0, 1)")
    d.setFullYear(date.year)
    d.setMonth(date.month.number - 1)
    d.setDate(date.day)
    val raw = d.toLocaleDateString("pt-BR", js("{ weekday: 'short', day: '2-digit', month: '2-digit' }")) as String
    return raw.replace(".", "")
}

private fun formatDatePtBr(day: Int, month: Int, year: Int): String {
    val d = js("new Date(2000, 0, 1)")
    d.setFullYear(year)
    d.setMonth(month - 1)
    d.setDate(day)
    return d.toLocaleDateString("pt-BR", js("{ day: 'numeric', month: 'long' }")) as String
}

private fun isBrazilWorldCupGame(game: Game, teams: Map<String, Team>): Boolean {
    if (game.competitionId != "worldCup") return false

    return teamMatchesId(resolveTeam(teams, game.homeTeamId), "brasil") ||
        teamMatchesId(resolveTeam(teams, game.awayTeamId), "brasil")
}
